import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Download, Share2, QrCode, Eye, Phone } from 'lucide-react';
import { 
  FaYoutube, FaWhatsapp, FaFacebook, FaInstagram, FaTelegram,
  FaPhone, FaGlobe
} from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../utils/imageUrl';
import { copyToClipboard } from '../utils/clipboard';
import SharePopup from '../components/SharePopup';

// Social platforms with 3D icon styling
const SOCIAL_PLATFORMS = [
  { id: 'youtube', name: 'YouTube', icon: FaYoutube, color: '#FF0000', gradient: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)', shadow: '0 8px 32px rgba(255,0,0,0.4)' },
  { id: 'whatsapp', name: 'WhatsApp', icon: FaWhatsapp, color: '#25D366', gradient: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', shadow: '0 8px 32px rgba(37,211,102,0.4)' },
  { id: 'facebook', name: 'Facebook', icon: FaFacebook, color: '#1877F2', gradient: 'linear-gradient(135deg, #1877F2 0%, #0D47A1 100%)', shadow: '0 8px 32px rgba(24,119,242,0.4)' },
  { id: 'instagram', name: 'Instagram', icon: FaInstagram, color: '#E4405F', gradient: 'linear-gradient(45deg, #405DE6, #5851DB, #833AB4, #C13584, #E1306C, #FD1D1D, #F56040, #F77737, #FCAF45, #FFDC80)', shadow: '0 8px 32px rgba(228,64,95,0.4)' },
  { id: 'telegram', name: 'Telegram', icon: FaTelegram, color: '#0088cc', gradient: 'linear-gradient(135deg, #0088cc 0%, #229ED9 100%)', shadow: '0 8px 32px rgba(0,136,204,0.4)' },
  { id: 'website', name: 'Website', icon: FaGlobe, color: '#6366F1', gradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)', shadow: '0 8px 32px rgba(99,102,241,0.4)' },
];

// Premium template colors
const PREMIUM_TEMPLATES = {
  'royal-blue-gold': { 
    primary: '#D4AF37', 
    secondary: '#0F4C5C', 
    background: 'linear-gradient(135deg, #0F4C5C 0%, #05386B 50%, #0A2647 100%)', 
    text: '#1a1a1a', 
    accent: '#FFD700',
    pattern: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D4AF37\' fill-opacity=\'0.08\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
  },
  'gold-royal': { 
    primary: '#D4AF37', 
    secondary: '#1a365d', 
    background: '#FDF5E6', 
    text: '#1a1a1a', 
    accent: '#ffd700',
    pattern: 'none'
  },
  'blue-gold': { 
    primary: '#D4AF37', 
    secondary: '#0f1419', 
    background: '#1a365d', 
    text: '#ffffff', 
    accent: '#ffd700',
    pattern: 'none'
  },
  'cream-classic': { 
    primary: '#8B4513', 
    secondary: '#2d1810', 
    background: '#FFFDD0', 
    text: '#1a1a1a', 
    accent: '#D4AF37',
    pattern: 'none'
  },
  'colorful-modern': { 
    primary: '#D4AF37', 
    secondary: '#1a1a1a', 
    background: '#FDF5E6', 
    text: '#1a1a1a', 
    accent: '#ff6b6b',
    pattern: 'none'
  }
};

// 3D Social Icon Component
const SocialIcon3D = ({ platform, link, position = 'left' }) => {
  const platformData = SOCIAL_PLATFORMS.find(p => p.id === platform) || SOCIAL_PLATFORMS[5];
  const Icon = platformData.icon;
  
  // Extract display URL
  const getDisplayUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return urlObj.hostname + urlObj.pathname;
    } catch {
      return url;
    }
  };

  return (
    <a 
      href={link} 
      target="_blank" 
      rel="noopener noreferrer"
      className={`flex flex-col items-center gap-2 group ${position === 'left' ? 'items-start' : 'items-end'}`}
    >
      {/* 3D Icon with golden border */}
      <div 
        className="relative w-16 h-16 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-all duration-300"
        style={{
          background: platformData.gradient,
          boxShadow: `${platformData.shadow}, inset 0 2px 10px rgba(255,255,255,0.3), 0 0 0 3px #D4AF37, 0 0 0 6px rgba(212,175,55,0.3)`,
          border: '2px solid rgba(255,255,255,0.2)'
        }}
      >
        {/* Glitter overlay */}
        <div 
          className="absolute inset-0 rounded-xl opacity-30"
          style={{
            background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%)',
            animation: 'shimmer 2s infinite'
          }}
        />
        <Icon className="w-8 h-8 text-white relative z-10 drop-shadow-lg" />
      </div>
      
      {/* URL label */}
      <span 
        className={`text-xs font-medium max-w-24 truncate ${position === 'left' ? 'text-left' : 'text-right'}`}
        style={{ 
          color: '#D4AF37',
          fontFamily: "'Brush Script MT', cursive",
          fontSize: '11px',
          textShadow: '0 1px 2px rgba(0,0,0,0.5)'
        }}
      >
        {getDisplayUrl(link)}
      </span>
    </a>
  );
};

// Ganesh Header Component
const GaneshHeader = ({ religiousText }) => (
  <div className="relative w-full">
    {/* Golden Arch */}
    <svg viewBox="0 0 400 100" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="goldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFD700" />
          <stop offset="50%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#B8860B" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      {/* Arch path */}
      <path 
        d="M 20 95 Q 200 5 380 95" 
        fill="none" 
        stroke="url(#goldGradient)" 
        strokeWidth="8"
        filter="url(#glow)"
      />
      <path 
        d="M 30 95 Q 200 15 370 95" 
        fill="none" 
        stroke="url(#goldGradient)" 
        strokeWidth="3"
        opacity="0.6"
      />
    </svg>
    
    {/* Ganesh Image */}
    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2">
      <div 
        className="w-20 h-20 rounded-full flex items-center justify-center text-4xl"
        style={{
          background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 50%, #B8860B 100%)',
          boxShadow: '0 4px 20px rgba(212,175,55,0.5), inset 0 2px 10px rgba(255,255,255,0.3)',
          border: '3px solid #FFD700'
        }}
      >
        🙏
      </div>
    </div>
    
    {/* Religious Text */}
    <div className="text-center mt-2">
      <p 
        className="text-sm font-bold whitespace-pre-line"
        style={{ 
          color: '#D4AF37',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          letterSpacing: '1px'
        }}
      >
        {religiousText || '!! SHRI GANESHAY NAMAH !!\n!! JAI JHULELA !!'}
      </p>
    </div>
  </div>
);

const PublicBioData = () => {
  const { biodataId } = useParams();
  const navigate = useNavigate();
  const bioCardRef = useRef(null);
  
  const [biodata, setBiodata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBiodata();
  }, [biodataId]);

  const fetchBiodata = async () => {
    try {
      const res = await api.get(`/biodata/public/${biodataId}`);
      setBiodata(res.data);
    } catch (error) {
      setError('Biodata not found or not published');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!bioCardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(bioCardRef.current, {
        scale: 4, // 4K quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#0F4C5C'
      });
      const link = document.createElement('a');
      link.download = `biodata-${biodata?.personal_info?.name || 'download'}-4K.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      
      await api.post(`/biodata/track-download/${biodataId}`).catch(() => {});
      toast.success('4K Biodata downloaded!');
    } catch (error) {
      toast.error('Failed to download');
    } finally {
      setDownloading(false);
    }
  };

  // Share URL for social media with proper OG tags
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const shareUrl = `${backendUrl}/api/share/biodata/${biodataId}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0F4C5C 0%, #05386B 100%)' }}>
        <div className="animate-spin w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !biodata) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0F4C5C 0%, #05386B 100%)' }}>
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Biodata Not Found</h1>
          <p className="text-gray-300 mb-6">{error}</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-yellow-500 text-black rounded-lg font-medium">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const personalInfo = biodata.personal_info || {};
  const familyInfo = biodata.family_info || {};
  const contactInfo = biodata.contact_info || {};
  const profilePhotos = biodata.profile_photos || [];
  const socialLinks = biodata.social_links || [];
  const customFields = biodata.custom_fields || [];

  // Split social links for left and right sides
  const leftLinks = socialLinks.filter((_, i) => i % 2 === 0);
  const rightLinks = socialLinks.filter((_, i) => i % 2 === 1);

  // OG Meta tags data - using backendUrl defined above
  const getOgImageUrl = () => {
    if (profilePhotos.length > 0 && profilePhotos[0].url) {
      const img = profilePhotos[0].url;
      if (img.startsWith('http')) return img;
      return `${backendUrl}${img}`;
    }
    return `${backendUrl}/static/og-default.png`;
  };
  const ogImageUrl = getOgImageUrl();
  const pageTitle = biodata.title || `${personalInfo.name || 'Premium'} Biodata`;
  const pageDescription = personalInfo.occupation 
    ? `${personalInfo.name} | ${personalInfo.occupation}${personalInfo.education ? ` | ${personalInfo.education}` : ''}`
    : `View ${personalInfo.name || 'this premium'} biodata`;

  return (
    <div 
      className="min-h-screen py-8 px-4" 
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #1a1a1a 100%)' }}
      data-testid="public-biodata-page"
    >
      {/* SEO Meta Tags for Social Sharing */}
      <Helmet>
        <title>{pageTitle} | ANANT Web Portal</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`${window.location.origin}/biodata/${biodataId}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${personalInfo.name || 'Biodata'} Profile Photo`} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      {/* Action Buttons */}
      <div className="max-w-4xl mx-auto mb-4 flex justify-center gap-3">
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-xl font-bold flex items-center gap-2 hover:from-yellow-400 hover:to-yellow-500 shadow-lg"
        >
          <Download className="w-5 h-5" />
          {downloading ? 'Generating 4K...' : 'Download 4K'}
        </button>
        <button
          onClick={() => setShowSharePopup(true)}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold flex items-center gap-2 hover:from-blue-400 hover:to-blue-500 shadow-lg"
          data-testid="biodata-share-btn"
        >
          <Share2 className="w-5 h-5" />
          Share
        </button>
        <button
          onClick={() => setShowQR(true)}
          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-bold flex items-center gap-2 hover:from-purple-400 hover:to-purple-500 shadow-lg"
        >
          <QrCode className="w-5 h-5" />
          QR
        </button>
      </div>

      {/* Premium Biodata Card */}
      <div className="max-w-4xl mx-auto">
        <div 
          ref={bioCardRef}
          className="relative overflow-hidden rounded-3xl"
          style={{ 
            background: 'linear-gradient(135deg, #0F4C5C 0%, #05386B 50%, #0A2647 100%)',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 2px #D4AF37'
          }}
        >
          {/* Background Pattern */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D4AF37\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          />

          {/* Main Content */}
          <div className="relative z-10 p-6">
            {/* Header with Ganesh */}
            <GaneshHeader religiousText={biodata.religious_text} />

            {/* BIODATA Title */}
            <h1 
              className="text-center text-4xl font-bold my-4"
              style={{ 
                color: '#D4AF37',
                textShadow: '0 4px 8px rgba(0,0,0,0.3), 0 0 20px rgba(212,175,55,0.3)',
                letterSpacing: '8px',
                fontFamily: "'Times New Roman', serif"
              }}
            >
              {biodata.title || 'BIODATA'}
            </h1>

            {/* Three Column Layout */}
            <div className="flex gap-4">
              {/* Left Social Icons */}
              <div className="w-24 flex flex-col gap-6 py-4">
                {leftLinks.length > 0 ? (
                  leftLinks.map((link, idx) => (
                    <SocialIcon3D key={idx} platform={link.platform} link={link.url} position="left" />
                  ))
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-xl bg-white/10 animate-pulse" />
                    <div className="w-16 h-16 rounded-xl bg-white/10 animate-pulse" />
                  </>
                )}
              </div>

              {/* Center Content */}
              <div 
                className="flex-1 rounded-2xl p-6"
                style={{
                  background: 'linear-gradient(180deg, #FDF5E6 0%, #FAF0E6 50%, #F5DEB3 100%)',
                  boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.1), 0 0 0 3px #D4AF37'
                }}
              >
                <div className="flex gap-4">
                  {/* Bio Details */}
                  <div className="flex-1 space-y-1">
                    {/* Personal Info */}
                    {[
                      { label: 'Name', value: personalInfo.name },
                      { label: 'Date of Birth', value: personalInfo.date_of_birth },
                      { label: 'Time of Birth', value: personalInfo.time_of_birth },
                      { label: 'Place of Birth', value: personalInfo.place_of_birth },
                      { label: 'Height', value: personalInfo.height },
                      { label: 'Occupation', value: personalInfo.occupation },
                      { label: 'Education', value: personalInfo.education },
                    ].filter(item => item.value).map((item, idx) => (
                      <div key={idx} className="flex text-sm">
                        <span className="w-28 text-gray-600 font-medium">{item.label}</span>
                        <span className="mx-2 text-gray-600">:</span>
                        <span 
                          className="flex-1 font-semibold"
                          style={{ 
                            color: '#B8860B',
                            fontFamily: "'Brush Script MT', cursive",
                            fontSize: '15px'
                          }}
                        >
                          {item.value}
                        </span>
                      </div>
                    ))}

                    {/* Family Info */}
                    {[
                      { label: 'Father', value: familyInfo.father_name },
                      { label: 'Occupation', value: familyInfo.father_occupation },
                      { label: 'Mother', value: familyInfo.mother_name },
                      { label: 'Brother', value: familyInfo.brother },
                      { label: 'Sister', value: familyInfo.sister },
                      { label: 'In Sindh', value: familyInfo.caste_community },
                    ].filter(item => item.value).map((item, idx) => (
                      <div key={idx} className="flex text-sm">
                        <span className="w-28 text-gray-600 font-medium">{item.label}</span>
                        <span className="mx-2 text-gray-600">:</span>
                        <span 
                          className="flex-1 font-semibold"
                          style={{ 
                            color: '#B8860B',
                            fontFamily: "'Brush Script MT', cursive",
                            fontSize: '15px'
                          }}
                        >
                          {item.value}
                        </span>
                      </div>
                    ))}

                    {/* Contact */}
                    {contactInfo.mobile_number && (
                      <div className="flex text-sm">
                        <span className="w-28 text-gray-600 font-medium">Contact No.</span>
                        <span className="mx-2 text-gray-600">:</span>
                        <span 
                          className="flex-1 font-semibold"
                          style={{ color: '#B8860B' }}
                        >
                          {contactInfo.mobile_number}
                        </span>
                      </div>
                    )}

                    {/* Custom Fields */}
                    {customFields.map((field, idx) => (
                      <div key={idx} className="flex text-sm">
                        <span className="w-28 text-gray-600 font-medium">{field.label}</span>
                        <span className="mx-2 text-gray-600">:</span>
                        <span 
                          className="flex-1 font-semibold"
                          style={{ color: '#B8860B' }}
                        >
                          {field.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Photos */}
                  {profilePhotos.length > 0 && (
                    <div className="w-32 flex flex-col gap-3">
                      {profilePhotos.map((photo, idx) => (
                        <div 
                          key={idx} 
                          className="rounded-lg overflow-hidden"
                          style={{
                            border: '3px solid #D4AF37',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                          }}
                        >
                          <img 
                            src={getImageUrl(photo.url)} 
                            alt="" 
                            className="w-full h-36 object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Address & Contact Section */}
                <div 
                  className="mt-4 p-4 rounded-xl grid grid-cols-2 gap-4"
                  style={{
                    background: 'linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0.2) 100%)',
                    border: '2px solid #D4AF37'
                  }}
                >
                  <div>
                    <p className="font-bold text-sm underline mb-2" style={{ color: '#B8860B' }}>
                      Residential Address
                    </p>
                    <p className="text-sm text-gray-700">{contactInfo.residential_address}</p>
                    <p className="text-sm text-gray-700">
                      {contactInfo.city}{contactInfo.state ? `, ${contactInfo.state}` : ''}
                    </p>
                  </div>
                  <div>
                    <p className="font-bold text-sm underline mb-2" style={{ color: '#B8860B' }}>
                      Contact No.
                    </p>
                    {contactInfo.mobile_number && (
                      <p className="text-sm text-gray-700 flex items-center gap-2">
                        <FaPhone className="w-3 h-3" style={{ color: '#25D366' }} />
                        {contactInfo.mobile_number}
                      </p>
                    )}
                    {contactInfo.secondary_number && (
                      <p className="text-sm text-gray-700 flex items-center gap-2">
                        <FaPhone className="w-3 h-3" style={{ color: '#25D366' }} />
                        {contactInfo.secondary_number}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Social Icons */}
              <div className="w-24 flex flex-col gap-6 py-4 items-end">
                {rightLinks.length > 0 ? (
                  rightLinks.map((link, idx) => (
                    <SocialIcon3D key={idx} platform={link.platform} link={link.url} position="right" />
                  ))
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-xl bg-white/10 animate-pulse" />
                    <div className="w-16 h-16 rounded-xl bg-white/10 animate-pulse" />
                  </>
                )}
              </div>
            </div>

            {/* QR Code */}
            {biodata.qr_enabled && biodata.qr_content && (
              <div className="flex justify-center mt-4">
                <div 
                  className="p-3 rounded-xl bg-white"
                  style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.3), 0 0 0 2px #D4AF37' }}
                >
                  <QRCodeSVG value={biodata.qr_content} size={80} level="M" />
                </div>
              </div>
            )}
          </div>

          {/* Golden Border Bottom */}
          <div 
            className="h-3"
            style={{
              background: 'linear-gradient(90deg, #B8860B 0%, #FFD700 25%, #D4AF37 50%, #FFD700 75%, #B8860B 100%)'
            }}
          />
        </div>
      </div>

      {/* View Count */}
      <div className="max-w-4xl mx-auto mt-4 text-center">
        <p className="text-gray-400 text-sm flex items-center justify-center gap-2">
          <Eye className="w-4 h-4" />
          {biodata.view_count || 0} views
        </p>
      </div>

      {/* Create CTA */}
      <div className="max-w-4xl mx-auto mt-6 text-center">
        <div 
          className="p-4 rounded-xl"
          style={{
            background: 'linear-gradient(135deg, rgba(212,175,55,0.2) 0%, rgba(212,175,55,0.1) 100%)',
            border: '1px solid rgba(212,175,55,0.3)'
          }}
        >
          <p className="text-white mb-2">Want to create your own premium biodata?</p>
          <button
            onClick={() => navigate('/biodata-builder')}
            className="px-8 py-3 rounded-xl font-bold text-black"
            style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
              boxShadow: '0 4px 15px rgba(212,175,55,0.4)'
            }}
          >
            Create Free Biodata
          </button>
        </div>
      </div>

      {/* QR Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowQR(false)}>
          <div className="bg-[#1a1a1a] rounded-2xl p-8 text-center text-white border-2 border-yellow-500" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#D4AF37' }}>Scan to View Biodata</h3>
            <div className="bg-white p-4 rounded-xl inline-block mb-4">
              <QRCodeSVG value={window.location.href} size={200} level="H" />
            </div>
            <p className="text-sm text-gray-400 mb-4 max-w-xs mx-auto truncate">{window.location.href}</p>
            <button 
              onClick={() => setShowQR(false)} 
              className="px-6 py-2 rounded-lg font-medium text-black"
              style={{ background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* CSS Animation for shimmer */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      
      {/* Share Popup */}
      <SharePopup 
        isOpen={showSharePopup}
        onClose={() => setShowSharePopup(false)}
        shareUrl={shareUrl}
        title={biodata?.title || `${personalInfo?.name || ''} Biodata`}
        description={personalInfo?.occupation || 'Premium Biodata'}
        itemType="biodata"
        itemId={biodataId}
      />
    </div>
  );
};

export default PublicBioData;
